import { useState } from 'react';
import { useJobNotes, useCreateJobNote, useUpdateJobNote, useDeleteJobNote } from '@/hooks/useJobNotes';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Panel } from '@/components/ui/panel';
import { Pencil, X, Check, Trash2 } from 'lucide-react';
import { authClient } from '@/lib/auth-client';

interface JobNotesCardProps {
  jobId: string;
}

interface JobNoteItemProps {
  note: {
    id: string;
    message: string;
    createdAt: string;
    user: {
      id: string;
      name: string;
      image?: string | null;
    };
  };
  currentUserId?: string;
  onUpdate: (id: string, message: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  isUpdating: boolean;
  isDeleting: boolean;
}

function JobNoteItem({ note, currentUserId, onUpdate, onDelete, isUpdating, isDeleting }: JobNoteItemProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedMessage, setEditedMessage] = useState(note.message);

  const canEdit = currentUserId === note.user.id;
  const isBusy = isUpdating || isDeleting;

  const handleSave = async () => {
    if (!editedMessage.trim() || editedMessage === note.message) {
      setIsEditing(false);
      setEditedMessage(note.message);
      return;
    }

    await onUpdate(note.id, editedMessage.trim());
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedMessage(note.message);
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this note?')) return;

    await onDelete(note.id);
    setIsEditing(false);
  };

  return (
    <div className="border-b border-gray-200 pb-4 last:border-b-0">
      <div className="flex items-start gap-3">
        {/* Note Content */}
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-baseline gap-2 mb-1">
            <span className="font-semibold text-gray-900 text-sm">
              {note.user.name}
            </span>
            <div className="flex items-center gap-2">
              <span className="text-gray-500 text-xs">
                {formatDistanceToNow(new Date(note.createdAt), { addSuffix: true })}
              </span>
              {canEdit && !isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  // -m-2 keeps the icon on the timestamp's baseline while the
                  // tappable box grows past a 12px pencil.
                  className="-m-2 p-2 text-gray-400 transition-colors hover:text-gray-600"
                  aria-label="Edit note"
                >
                  <Pencil className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
          {isEditing ? (
            <div className="space-y-2">
              <Textarea
                value={editedMessage}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEditedMessage(e.target.value)}
                // text-base below md: anything smaller makes iOS Safari zoom the
                // viewport when the field takes focus.
                className="min-h-[60px] text-base md:text-sm"
                disabled={isBusy}
              />
              <div className="flex items-center justify-between gap-2">
                <button
                  onClick={handleDelete}
                  disabled={isBusy}
                  className="p-2 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                  aria-label="Delete note"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={handleCancel}
                    disabled={isBusy}
                    className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
                    aria-label="Cancel"
                  >
                    <X className="h-4 w-4" />
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={!editedMessage.trim() || isBusy}
                    className="p-2 text-blue-600 hover:text-blue-800 transition-colors disabled:opacity-50"
                    aria-label="Save"
                  >
                    <Check className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-gray-700 text-sm whitespace-pre-wrap break-words">
              {note.message}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export function JobNotesCard({ jobId }: JobNotesCardProps) {
  const [message, setMessage] = useState('');
  const { data: notes, isLoading } = useJobNotes(jobId);
  const createNote = useCreateJobNote();
  const updateNote = useUpdateJobNote(jobId);
  const deleteNote = useDeleteJobNote(jobId);
  const { data: session } = authClient.useSession();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!message.trim()) return;

    await createNote.mutateAsync({
      jobId,
      message: message.trim(),
    });

    setMessage('');
  };

  const handleUpdate = async (id: string, updatedMessage: string) => {
    await updateNote.mutateAsync({
      id,
      message: updatedMessage,
    });
  };

  const handleDelete = async (id: string) => {
    await deleteNote.mutateAsync(id);
  };

  return (
    <Panel title="💬 Notes" className="flex flex-col" contentClassName="">
      {/* Notes Display */}
      <div className="space-y-4 mb-6 flex-1 overflow-y-auto">
        {isLoading ? (
          <p className="text-gray-500 text-sm">Loading notes...</p>
        ) : notes && notes.length > 0 ? (
          notes.map((note) => (
            <JobNoteItem
              key={note.id}
              note={note}
              currentUserId={session?.user?.id}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
              isUpdating={updateNote.isPending}
              isDeleting={deleteNote.isPending}
            />
          ))
        ) : (
          <p className="text-gray-500 text-sm">No notes yet. Be the first to add one!</p>
        )}
      </div>

      {/* Add Note Form */}
      <form onSubmit={handleSubmit} className="space-y-3">
        <Textarea
          value={message}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setMessage(e.target.value)}
          placeholder="Add a note for team communication..."
          className="min-h-[75px] resize-none"
          disabled={createNote.isPending}
        />
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={!message.trim() || createNote.isPending}
          >
            {createNote.isPending ? 'Adding...' : 'Add Note'}
          </Button>
        </div>
      </form>
    </Panel>
  );
}
